import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  acceptHandover,
  CASES_KEY,
  declineHandover,
  INCOMING_KEY,
  NOTICES_KEY,
  ProblemError,
  RECORD_KEY,
  refusalCode,
  useIncomingHandovers,
  type IncomingHandoverOut,
} from "@/api";
import { Button } from "@/components/Button";
import { TextArea } from "@/components/fields/TextArea";
import { Figures } from "@/components/Figures";
import { StageChip } from "@/components/stages/StageChip";
import { StateMessage } from "@/components/StateMessage";
import { VolumeFigure } from "@/components/VolumeFigure";
import { activityTime, formatDay } from "@/lib/format";

import { stop } from "./HandoverNotices";
import styles from "./Incoming.module.css";
import { useArrivals } from "./useArrivals";

type Gesture = "accept" | "decline";

/**
 * What the live line says, and where a sighted reader finds it: under the
 * heading once the card it concerns is gone (`section`), on the card that
 * survived a failed answer (`card`), or nowhere but in the reader's ear —
 * an arrival, where the new card is the thing to see (`none`). Shown in the
 * one notice pattern of the record and the handover (UX gate round 1, #3):
 * `success` for an answer that went through, `neutral` for news (the request
 * met a state it could not be answered in), `conflict` for an answer that did
 * not get through.
 */
interface Said {
  text: string;
  place: "section" | "card" | "none";
  cardId: string | null;
  tone: "neutral" | "success" | "conflict";
}

const SILENT: Said = { text: "", place: "none", cardId: null, tone: "neutral" };

/**
 * After a card left: focus its neighbour, or the heading. After „Abbrechen": back to „Ablehnen …".
 * After a successful „Erneut laden", whose button is gone with the failure: the heading.
 */
type FocusWish = { kind: "past"; id: string; order: string[] } | { kind: "decline"; id: string } | { kind: "heading" };

/** Said once under the heading, and the description of every „Annehmen" (Task 13 review, M4). */
const EFFECT = "Nimmst du an, liegt der Vorgang bei dir — mit Akte, Kalkulation und Freigabe.";
const EFFECT_ID = "incoming-effect";

/** Local calendar days from `now` to `iso`; a stamp already past counts as today. */
function daysUntil(iso: string, now: Date): number {
  const at = new Date(iso);
  const from = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const to = new Date(at.getFullYear(), at.getMonth(), at.getDate()).getTime();
  return Math.max(0, Math.round((to - from) / 86_400_000));
}

/**
 * „verfällt heute" / „verfällt morgen" once it is near (Task 13 review, M5):
 * a date has to be worked out against today, and on the last day that sum is
 * the whole point. Further out, the date.
 */
export function expiryWords(iso: string, now: Date = new Date()): string {
  const days = daysUntil(iso, now);
  if (days === 0) return "verfällt heute";
  if (days === 1) return "verfällt morgen";
  return `verfällt am ${formatDay(iso)}`;
}

function handoverId(item: IncomingHandoverOut): string {
  return item.handover.id;
}

function customerName(item: IncomingHandoverOut): string | null {
  return item.customer !== null ? (item.customer.company ?? item.customer.displayName) : null;
}

/**
 * Why a request can no longer be answered (contract §3.5: a 409 names the
 * state it met, `reasons=[state]`, or the code of the refusal; a 404 means
 * it is not there for me). `null`: the request still stands — the failure
 * was on the way, and the card stays to be answered again.
 */
function goneSentence(item: IncomingHandoverOut, cause: unknown): string | null {
  const of = `Die Übergabe von „${item.title}“`;
  const from = item.handover.fromDisplayName;
  if (cause instanceof ProblemError && cause.status === 404) return `${of} gibt es nicht mehr.`;
  switch (refusalCode(cause)) {
    case "expired":
      return `${of} ist inzwischen verfallen.`;
    case "withdrawn":
      return `${from} hat die Übergabe von „${item.title}“ inzwischen zurückgezogen.`;
    case "declined":
      return `${of} hast du schon abgelehnt.`;
    case "accepted":
      return `${of} hast du schon angenommen — der Vorgang liegt bei dir.`;
    case "not_owner_anymore":
      return `${of} entfällt — der Vorgang liegt nicht mehr bei ${from}.`;
    case "case_finished":
      return `${of} entfällt — der Vorgang ist beendet.`;
    default:
      return null;
  }
}

/** The request still stands, the answer did not get through — say so, and what to do. */
function failureSentence(gesture: Gesture, cause: unknown): string {
  const verb = gesture === "accept" ? "Nicht angenommen" : "Nicht abgelehnt";
  return cause instanceof ProblemError
    ? `${verb}: ${stop(cause.userMessage)} Versuch es gleich noch einmal.`
    : `${verb} — der Server ist nicht erreichbar. Versuch es gleich noch einmal.`;
}

/**
 * „Übergabe an dich" (design §8.3, UX ruling 11): only there when something
 * waits. One card per request in the panel recipe — the Vorgang, who asks,
 * since when, until when, the message as a quote — and the two answers.
 * „Annehmen" opens the record straight away, with the sentence of what just
 * happened; „Ablehnen …" asks for an optional reason first.
 *
 * A request that lapsed or was taken back while this page stood open
 * disappears at once, with a sentence (review focus 4) — the refetch alone
 * would leave a card that can no longer be answered. A failure on the way
 * leaves the card and says so on it. Focus never falls to the page: while
 * an answer is under way the buttons are `aria-disabled` (a `disabled`
 * button drops the focus it holds), and a card that leaves hands focus to
 * its neighbour or the heading.
 *
 * While the list loads, nothing is drawn: an empty box is the common case,
 * and a reserved one would collapse again on most visits. The start page
 * holds back what stands below this block until the list has answered
 * (`StartScreen`), so a late card moves nothing already on screen.
 */
export function IncomingHandovers() {
  const incoming = useIncomingHandovers();
  const client = useQueryClient();
  const navigate = useNavigate();
  const [gone, setGone] = useState<ReadonlySet<string>>(new Set());
  const [said, setSaid] = useState<Said>(SILENT);
  const [declining, setDeclining] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState<{ id: string; gesture: Gesture } | null>(null);
  // Bumped when a reload came back: the render it causes carries the focus wish.
  const [, setReloaded] = useState(0);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const titles = useRef(new Map<string, HTMLHeadingElement>());
  const declineButtons = useRef(new Map<string, HTMLButtonElement>());
  const focusWish = useRef<FocusWish | null>(null);

  const list = Array.isArray(incoming.data) ? incoming.data : undefined;
  const items = (list ?? []).filter((item) => !gone.has(item.handover.id));

  useArrivals(list, handoverId, (arrived) => {
    const [first] = arrived;
    if (first === undefined) return;
    setSaid({
      text:
        arrived.length === 1
          ? `Neue Übergabe an dich: „${first.title}“ von ${first.handover.fromDisplayName}.`
          : `${arrived.length} neue Übergaben an dich.`,
      place: "none",
      cardId: null,
      tone: "neutral",
    });
  });

  // After the render that took a card away or closed a form: focus where
  // the reader continues, never the page body.
  useEffect(() => {
    const wish = focusWish.current;
    if (wish === null) return;
    focusWish.current = null;
    if (wish.kind === "decline") {
      declineButtons.current.get(wish.id)?.focus();
      return;
    }
    if (wish.kind === "heading") {
      headingRef.current?.focus();
      return;
    }
    const at = wish.order.indexOf(wish.id);
    const nearest = [...wish.order.slice(at + 1), ...wish.order.slice(0, Math.max(at, 0)).reverse()];
    const next = nearest.map((id) => titles.current.get(id)).find((title) => title !== undefined);
    (next ?? headingRef.current)?.focus();
  });

  /** Everything an answer can change: my requests, my notices, my register, the record itself. */
  function refresh(item: IncomingHandoverOut) {
    void client.invalidateQueries({ queryKey: INCOMING_KEY });
    void client.invalidateQueries({ queryKey: NOTICES_KEY });
    void client.invalidateQueries({ queryKey: CASES_KEY });
    void client.invalidateQueries({ queryKey: RECORD_KEY(item.handover.caseId) });
  }

  function letGo(item: IncomingHandoverOut, sentence: string, tone: Said["tone"]) {
    const id = item.handover.id;
    focusWish.current = { kind: "past", id, order: items.map(handoverId) };
    setGone((previous) => new Set(previous).add(id));
    if (declining === id) {
      setDeclining(null);
      setReason("");
    }
    setSaid({ text: sentence, place: "section", cardId: null, tone });
  }

  function refused(item: IncomingHandoverOut, gesture: Gesture, cause: unknown) {
    refresh(item);
    const sentence = goneSentence(item, cause);
    if (sentence !== null) {
      letGo(item, sentence, "neutral");
      return;
    }
    setSaid({ text: failureSentence(gesture, cause), place: "card", cardId: item.handover.id, tone: "conflict" });
  }

  function open(item: IncomingHandoverOut, notice: string) {
    refresh(item);
    navigate(`/vorgang/${item.handover.caseId}`, { state: { notice } });
  }

  async function accept(item: IncomingHandoverOut) {
    if (busy !== null) return;
    setBusy({ id: item.handover.id, gesture: "accept" });
    setSaid(SILENT);
    try {
      await acceptHandover(item.handover.id);
      open(item, `Du hast „${item.title}“ von ${item.handover.fromDisplayName} übernommen.`);
    } catch (cause) {
      setBusy(null);
      // Taken over a moment ago in another tab: the Vorgang is mine, which
      // is what this click wanted — open it rather than report a refusal.
      if (refusalCode(cause) === "accepted") {
        open(item, `Du hast „${item.title}“ schon übernommen.`);
        return;
      }
      refused(item, "accept", cause);
    }
  }

  async function decline(item: IncomingHandoverOut) {
    if (busy !== null) return;
    setBusy({ id: item.handover.id, gesture: "decline" });
    setSaid(SILENT);
    const text = reason.trim();
    try {
      await declineHandover(item.handover.id, text === "" ? null : text);
      refresh(item);
      letGo(item, `Übergabe von „${item.title}“ abgelehnt.`, "success");
    } catch (cause) {
      refused(item, "decline", cause);
    } finally {
      setBusy(null);
    }
  }

  function startDeclining(id: string) {
    if (busy !== null) return;
    setReason("");
    setDeclining(id);
    if (said.place === "card") setSaid(SILENT);
  }

  function stopDeclining(id: string) {
    if (busy !== null) return;
    focusWish.current = { kind: "decline", id };
    setDeclining(null);
    setReason("");
    // „Nicht abgelehnt …" answered the form that is now closed.
    if (said.place === "card" && said.cardId === id) setSaid(SILENT);
  }

  /**
   * „Erneut laden" leaves with the failure it answers, and a button that
   * unmounts under the focus drops it to the page (Task 13 review, I3). So a
   * reload that came back puts focus on the heading — and if nothing waits,
   * the heading stays with a sentence saying so, rather than the whole block
   * vanishing under the reader. A reload that failed again keeps its button,
   * and the focus on it.
   */
  function reload() {
    void incoming.refetch().then((result) => {
      if (result.isError) return;
      const waiting = Array.isArray(result.data) ? result.data.filter((item) => !gone.has(item.handover.id)) : [];
      if (waiting.length === 0) {
        setSaid({ text: "Gerade wartet keine Übergabe auf dich.", place: "section", cardId: null, tone: "neutral" });
      }
      focusWish.current = { kind: "heading" };
      setReloaded((count) => count + 1);
    });
  }

  // Without data, React Query puts a query that fetches again back to
  // `pending`, error and all — so „failed" also covers the fetch after a
  // failure (`errorUpdateCount`). Otherwise the block would vanish for the
  // length of every retry, and take the focused „Erneut laden" with it.
  const failed = list === undefined && (incoming.isError === true || (incoming.errorUpdateCount ?? 0) > 0);
  // A failure said on a card that has since gone (a refetch took it away)
  // moves up under the heading — otherwise it would vanish unread.
  const onCard = said.place === "card" && items.some((item) => item.handover.id === said.cardId);
  const underHeading = said.text !== "" && (said.place === "section" || (said.place === "card" && !onCard));

  if (items.length === 0 && !underHeading && !failed) return null;

  return (
    <section className={styles.inbox} aria-labelledby="incoming-handovers">
      <h2 id="incoming-handovers" ref={headingRef} tabIndex={-1} className={`label ${styles.head}`}>
        {/* The mesh marker before a section title (design 2026-10-24 §8.6). */}
        <span className="marke" aria-hidden="true" />
        Übergabe an dich
        {items.length > 0 ? (
          <>
            {" · "}
            <span className="tabular">{items.length}</span>
          </>
        ) : null}
      </h2>
      {/* The live region stands from the first render, the notice inside it
          comes and goes (F-13: a region mounted together with its text is
          not reliably read out). Under the heading it is seen, in the one
          notice pattern (UX gate round 1, #3); otherwise only heard. */}
      <div role="status" className={underHeading ? undefined : "visually-hidden"}>
        {said.text !== "" ? <StateMessage tone={said.tone} live={false} title={said.text} /> : null}
      </div>
      {items.length > 0 ? (
        <p id={EFFECT_ID} className={styles.effect}>
          {EFFECT}
        </p>
      ) : null}
      {failed ? (
        // A fetch that failed, in the one notice pattern (UX gate round 1, #3) —
        // neutral: not a refusal, like the register's and the record's.
        <StateMessage
          tone="neutral"
          live={false}
          title="Anfragen an dich sind gerade nicht abrufbar — versuch es gleich noch einmal."
          action={
            <Button
              variant="quiet"
              flush
              aria-disabled={incoming.isFetching === true ? true : undefined}
              onClick={reload}
            >
              {incoming.isFetching === true ? "Wird geladen …" : "Erneut laden"}
            </Button>
          }
        />
      ) : null}
      {items.length > 0 ? (
        <ul className={styles.cards}>
          {items.map((item) => {
            const id = item.handover.id;
            const customer = customerName(item);
            const cardText = onCard && said.cardId === id ? said.text : null;
            const saidId = `incoming-${id}-said`;
            const busyHere = busy?.id === id ? busy.gesture : null;
            const waiting = busy !== null ? true : undefined;
            return (
              <li key={id} className={styles.card}>
                {/* One column from the left edge (UX gate round 3, #10): the
                    title line carries the stage and the amount right after
                    the Vorgang, the quote and the answers run under it — no
                    part parked in a corner of its own. */}
                <div className={styles.cardHead}>
                  <div className={styles.cardLine}>
                    <h3
                      className={styles.cardTitle}
                      tabIndex={-1}
                      ref={(node) => {
                        if (node !== null) titles.current.set(id, node);
                        else titles.current.delete(id);
                      }}
                    >
                      <span className={styles.cardName}>{item.title}</span>{" "}
                      <span className={`num ${styles.cardNo}`}>{item.caseNo}</span>
                      {/* „kein Kunde" as in the register and the recent list (S-10, UX gate round 1, #14).
                          The name in a box of its own (round-4 screen check, R4-9): it
                          moves to the next line whole rather than break inside itself —
                          and its „·" with it (round-5 screen check, R5-8): at 390 px the
                          dot hung at the end of the line above. */}{" "}
                      <span className={`${styles.cardCustomer} ${customer === null ? styles.noCustomer : ""}`}>
                        {"· "}
                        <span className={styles.cardCustomerName}>{customer ?? "kein Kunde"}</span>
                      </span>
                    </h3>
                    <span className={styles.cardFacts}>
                      <StageChip status={item.status} />
                      <VolumeFigure volume={item.volume} missing="words" status={item.status} />
                    </span>
                  </div>
                  {/* Time and date in the figure face (UX gate round 1, #13). */}
                  {/* Each fact whole, its „·" leading it (R5-8): a line never
                      ends in a dot or „verfällt am" without its date. */}
                  <p className={styles.sub}>
                    <span className={styles.fact}>von {item.handover.fromDisplayName}</span>{" "}
                    <span className={styles.fact}>
                      {"· "}
                      <Figures text={`seit ${activityTime(item.handover.requestedAt)}`} />
                    </span>{" "}
                    <span className={styles.fact}>
                      {"· "}
                      <Figures text={expiryWords(item.handover.expiresAt)} />
                    </span>
                  </p>
                </div>

                <blockquote className={styles.quote}>{item.handover.message}</blockquote>

                {declining === id ? (
                  <form
                    className={styles.form}
                    onSubmit={(event) => {
                      event.preventDefault();
                      void decline(item);
                    }}
                    onKeyDown={(event) => {
                      if (event.key !== "Escape") return;
                      event.preventDefault();
                      stopDeclining(id);
                    }}
                  >
                    <TextArea
                      id={`decline-${id}`}
                      label="Grund (freiwillig)"
                      hint={`${item.handover.fromDisplayName} behält den Vorgang und liest deine Antwort auf der Startseite.`}
                      value={reason}
                      onChange={setReason}
                      rows={2}
                      maxLength={500}
                      autoFocus
                    />
                    <div className={styles.actions}>
                      <Button aria-disabled={waiting} onClick={() => stopDeclining(id)}>
                        Abbrechen
                      </Button>
                      {/* Ink, not red (design 2026-10-24 §8.2): the reason
                          form's own answer, beside the card's red
                          „Annehmen" when it closes. Under way it is `busy`
                          and keeps its ink (Task 13 review, M6). */}
                      <Button
                        type="submit"
                        variant="tinte"
                        busy={busyHere === "decline"}
                        aria-disabled={waiting}
                        aria-describedby={cardText !== null ? saidId : undefined}
                      >
                        {busyHere === "decline" ? "Wird abgelehnt …" : "Ablehnen"}
                      </Button>
                    </div>
                  </form>
                ) : (
                  <div className={styles.actions}>
                    <Button
                      aria-disabled={waiting}
                      ref={(node) => {
                        if (node !== null) declineButtons.current.set(id, node);
                        else declineButtons.current.delete(id);
                      }}
                      onClick={() => startDeclining(id)}
                    >
                      Ablehnen …
                    </Button>
                    {/* The start page's main action (Task 11, UX-Gutachten
                        1, B-8): red, where it was black and in the dark
                        white. Ink while the card says the answer did not
                        get through — status red and K60 red never stand
                        together (CI §2.1). Under way it is `busy` and keeps
                        its colours (Task 13 review, M6). */}
                    <Button
                      variant={cardText !== null ? "tinte" : "primary"}
                      busy={busyHere === "accept"}
                      aria-disabled={waiting}
                      aria-describedby={cardText !== null ? `${EFFECT_ID} ${saidId}` : EFFECT_ID}
                      onClick={() => void accept(item)}
                    >
                      {busyHere === "accept" ? "Wird angenommen …" : "Annehmen"}
                    </Button>
                  </div>
                )}

                {/* The answer that did not get through, on its card — the live
                    line above says it aloud, this is where it is seen. */}
                {cardText !== null ? <StateMessage id={saidId} tone="conflict" live={false} title={cardText} /> : null}
              </li>
            );
          })}
        </ul>
      ) : null}
    </section>
  );
}
